import { faCheck, faSync, faChevronDown, faBan } from "@fortawesome/pro-light-svg-icons";
import { Typography, ButtonGroup, Button, Menu, MenuList, Divider, makeStyles } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { concat, NEVER, of, merge, interval } from "rxjs";
import { switchMap, map, tap, withLatestFrom, skip } from "rxjs/operators";
import { useObservableCallback, useObservableState, useObservable, pluckFirst } from "observable-hooks";
import type { IResponseWithLoadingAndTimestamp } from "App/Modules/Models";
import { HumanizeDateTime } from "../HumanizeDateTime";
import { MenuItemWithIcon } from "../Menu/MenuItemWithIcon";

const useStyles = makeStyles((theme) => ({
    refreshButtonGroup: {
        marginLeft: theme.spacing(1),
    },
    refreshButtonGroupError: {
        outline: `1px solid ${theme.palette.error.main}`,
    },
    refreshMenuList: {
        paddingBottom: 0,
        paddingTop: 0,
    },
    refreshMenuListInner: {
        outline: 0,
    },
}));

export type RefreshIntervalSeconds = 0 | 30 | 60 | 300 | 900 | 1800;

type RefreshButtonProps = IResponseWithLoadingAndTimestamp<void> & {
    className?: string;
    onRefresh: (refreshIntervalSeconds: RefreshIntervalSeconds) => void;
    refreshIntervalSeconds?: RefreshIntervalSeconds;
};

export const RefreshButton = (props: RefreshButtonProps) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const isLoading = props.isLoading || props.isPartiallyLoading;
    const hasError = !isLoading && (props.hasError || props.hasPartialError);

    const [refreshMenuAnchorEl, setRefreshMenuAnchorEl] = React.useState<HTMLButtonElement>(null);
    const [refreshMenuOpen, setRefreshMenuOpen] = useState(false);

    const refreshIntervalProp$ = useObservable(pluckFirst, [props.refreshIntervalSeconds]);
    const isLoading$ = useObservable(pluckFirst, [isLoading]);

    const [onRefreshInterval, refreshInterval$] = useObservableCallback<RefreshIntervalSeconds>((ri$) =>
        merge(refreshIntervalProp$.pipe(skip(1)), ri$).pipe(
            tap(() => setRefreshMenuOpen(false)),
            withLatestFrom(isLoading$),
            switchMap(([refreshInterval, isCurrentlyLoading]) => {
                if (isCurrentlyLoading) {
                    return NEVER;
                }

                if (refreshInterval === 0 || refreshInterval === undefined) {
                    return of<RefreshIntervalSeconds>(0);
                }

                return concat(
                    of<RefreshIntervalSeconds>(0),
                    interval(refreshInterval * 1000).pipe(map(() => refreshInterval as RefreshIntervalSeconds)),
                );
            }),
            tap((ri) => {
                if (props.onRefresh) {
                    (window as any).requestIdleCallback(
                        () => {
                            props.onRefresh(ri);
                        },
                        { timeout: 1000 },
                    );
                }
            }),
        ),
    );

    const refreshIntervalSeconds = useObservableState(refreshInterval$, props.refreshIntervalSeconds ?? 0);

    const handleRefreshMenuClose = () => {
        setRefreshMenuOpen(false);
    };

    const handleRefreshMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setRefreshMenuAnchorEl(event.currentTarget);
        setRefreshMenuOpen((open) => !open);
    };

    const refreshIntervalListItem = (refreshInterval: RefreshIntervalSeconds) => (
        <MenuItemWithIcon
            icon={refreshInterval === refreshIntervalSeconds && faCheck}
            key={refreshInterval}
            onClick={() => onRefreshInterval(refreshInterval)}
        >
            {t(`Gui.Common.RefreshInterval.Interval${refreshInterval}`)}
        </MenuItemWithIcon>
    );

    const buttonGroupClass = cx(classes.refreshButtonGroup, hasError && classes.refreshButtonGroupError);

    return (
        <div className={props.className}>
            {props.dtLastUpdated && !isLoading && (
                <Typography variant="body2" color="textSecondary">
                    {t("Gui.Common.LastUpdated")}: <HumanizeDateTime dateTime={props.dtLastUpdated} />
                </Typography>
            )}
            {props.onRefresh && isLoading && (
                <Typography variant="body2" color="textSecondary">
                    {t("Gui.Common.Labels.RefreshingData")}
                </Typography>
            )}
            {props.onRefresh && (
                <>
                    <ButtonGroup size="small" className={buttonGroupClass} variant="contained">
                        <Button
                            startIcon={
                                <FontAwesomeIcon
                                    icon={hasError && !isLoading ? faBan : faSync}
                                    fixedWidth
                                    spin={isLoading && refreshIntervalSeconds === 0}
                                />
                            }
                            onClick={() => onRefreshInterval(0)}
                        >
                            {t("Gui.Buttons.Reload")}
                        </Button>
                        <Button onClick={(event) => handleRefreshMenuClick(event)}>
                            <FontAwesomeIcon icon={faChevronDown} />
                        </Button>
                    </ButtonGroup>
                    <Menu
                        open={refreshMenuOpen}
                        classes={{ list: classes.refreshMenuList }}
                        onClose={() => handleRefreshMenuClose()}
                        anchorEl={refreshMenuAnchorEl}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <MenuList dense classes={{ root: classes.refreshMenuListInner }} role="menu">
                            {([30, 60, 300, 900, 1800] as RefreshIntervalSeconds[]).map((i) =>
                                refreshIntervalListItem(i),
                            )}
                        </MenuList>
                        <Divider />
                        <MenuList dense classes={{ root: classes.refreshMenuListInner }} role="menu">
                            {refreshIntervalListItem(0)}
                        </MenuList>
                    </Menu>
                </>
            )}
        </div>
    );
};
